import { useStripe } from '@stripe/react-stripe-js'
import { CanMakePaymentResult } from '@stripe/stripe-js'
import {
  PaymentMethod,
  setPaymentRequest,
  setXPayPaymentMethod,
  submitXPayPayment,
  TransactionStatus,
  updateTransaction,
} from 'store/billing'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useEffect } from 'react'
import { batch } from 'react-redux'
import getPriceDetailsForCurrency from 'utils/getPriceDetailsForCurrency'
import { useGetProductsQuery } from 'store/api/billing/products'
import { PricePoint, Product } from 'store/api/billing/payments.interface'
import useGetUser from 'components/Dashboard/utils/useGetUser'

export default function usePaymentRequest(): void {
  const dispatch = useAppDispatch()
  const { priceInterval, selectedProductPk, selectedCurrency, promoMode, promoCode } =
    useAppSelector(s => s.products)
  const { data: productsData } = useGetProductsQuery({ promoMode, promoCode })
  const selectedPricePoint = productsData?.products
    ?.find((p: Product) => p.PK === selectedProductPk)
    ?.price_points.find((pp: PricePoint) => pp.duration === priceInterval)
  const selectedPrice =
    selectedPricePoint && getPriceDetailsForCurrency(selectedPricePoint, selectedCurrency?.code)
  const paymentRequest = useAppSelector(s => s.payments.paymentRequest)
  const { data: userData } = useGetUser()

  const stripe = useStripe()

  useEffect(() => {
    !paymentRequest?.isShowing() &&
      paymentRequest?.update({
        currency: selectedCurrency?.code ?? 'usd',
        total: {
          label: 'Total',
          amount: (selectedPrice?.price ?? 0) * (selectedCurrency?.code === 'jpy' ? 1 : 100),
        },
      })
  }, [paymentRequest, selectedCurrency?.code, selectedPrice])

  useEffect(() => {
    const checkAndSetPaymentRequest = async () => {
      const pr = stripe?.paymentRequest({
        country: selectedCurrency?.country_code ?? 'US',
        currency: selectedCurrency?.code ?? 'usd',
        total: {
          label: 'Total',
          amount: (selectedPrice?.price ?? 0) * (selectedCurrency?.code === 'jpy' ? 1 : 100),
        },
      })

      const result: CanMakePaymentResult | undefined | null = await pr?.canMakePayment()
      if (result) {
        const xpayMethod = (() => {
          /**
           * For local testing with apple pay please make sure to verify your domain
           * https://stripe.com/docs/stripe-js/elements/payment-request-button?html-or-react=html#verifying-your-domain-with-apple-pay
           *
           * Add a test domain here: https://dashboard.stripe.com/settings/payments/apple_pay
           */
          // @ts-ignore _canMakePaymentAvailability is not exposed in the Stripe types
          if (result.applePay || pr._canMakePaymentAvailability.APPLE_PAY) {
            return PaymentMethod.APPLE
          }
          if (
            result.googlePay ||
            //@ts-ignore
            pr._canMakePaymentAvailability.GOOGLE_PAY
          ) {
            return PaymentMethod.GOOGLE
          }
          //@ts-ignore
          if (result.browserPay || pr._canMakePaymentAvailability.BROWSER) {
            return PaymentMethod.BROWSER
          }
          return
        })()

        pr?.once('paymentmethod', async event => {
          dispatch(submitXPayPayment({ event, email: userData?.email }))
        })

        pr?.on('cancel', () => {
          dispatch(
            updateTransaction({
              transactionStatus: TransactionStatus.FAILURE,
              errorMessage: 'Payment cancelled',
            }),
          )
        })

        batch(() => {
          dispatch(setXPayPaymentMethod(xpayMethod))
          dispatch(setPaymentRequest(pr))
        })
      }
    }
    // this should only run the first time when paymentRequest is undefined
    if (!paymentRequest) {
      checkAndSetPaymentRequest()
    }
  }, [
    dispatch,
    paymentRequest,
    selectedCurrency?.country_code,
    selectedCurrency?.code,
    selectedPrice,
    stripe,
    userData?.email,
  ])
}
